import React, { useState, useEffect, useCallback } from 'react';
import T from '../styles/theme';
import { Badge, Btn, ProgressBar } from '../components/UI';
import Icon from '../components/Icon';
import { useAuth } from '../data/auth';
import { useToast } from '../components/Toast';
import supabase from '../data/supabase';
import apiFetch from '../data/api';

/* ═══════════════════════════════════════════════════════
   THE PROSPECTOR — Dashboard & Control Panel
   Visual monitoring, domain health, ICP config,
   email templates, and auto-reply settings
   ═══════════════════════════════════════════════════════ */

const cfDate = (d) => { if (!d) return ""; const dt = new Date(d); return (dt.getMonth()+1)+"/"+dt.getDate()+"/"+String(dt.getFullYear()).slice(-2); };

// Snov.io filter options (mapped to their API values)
const SENIORITY_LEVELS = [
  { v: "c_level", l: "C-Level" },
  { v: "vp_level", l: "VP" },
  { v: "director_level", l: "Director" },
  { v: "manager_level", l: "Manager" },
  { v: "senior", l: "Senior" },
  { v: "staff", l: "Staff" },
];
const DEPARTMENTS = [
  { v: "sales", l: "Sales" },
  { v: "marketing", l: "Marketing" },
  { v: "operations", l: "Operations" },
  { v: "finance_administration", l: "Finance & Admin" },
  { v: "engineering", l: "Engineering" },
  { v: "human_resources", l: "Human Resources" },
  { v: "it_is", l: "IT & IS" },
  { v: "product_design", l: "Product & Design" },
  { v: "customer_success", l: "Customer Success" },
];
const COMPANY_SIZES = [
  { v: "1-10", l: "1-10" },
  { v: "11-50", l: "11-50" },
  { v: "51-200", l: "51-200" },
  { v: "201-500", l: "201-500" },
  { v: "501-1000", l: "501-1000" },
  { v: "1001-5000", l: "1001-5000" },
  { v: "5001-10000", l: "5001-10000" },
  { v: "10001+", l: "10000+" },
];
const REVENUE_RANGES = [
  { v: "0-1m", l: "$0-1M" },
  { v: "1m-10m", l: "$1M-10M" },
  { v: "10m-50m", l: "$10M-50M" },
  { v: "50m-100m", l: "$50M-100M" },
  { v: "100m-250m", l: "$100M-250M" },
  { v: "250m-500m", l: "$250M-500M" },
  { v: "500m-1b", l: "$500M-1B" },
  { v: "1b+", l: "$1B+" },
];

// Comma-separated input that lets you type freely and only splits on blur
function CommaInput({ value, onCommit, style, placeholder }) {
  const [text, setText] = useState((value || []).join(", "));
  // Sync external changes when component re-renders with new value
  useEffect(() => {
    setText((value || []).join(", "));
  }, [value]);
  return (
    <input
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={() => onCommit(text.split(",").map(s => s.trim()).filter(Boolean))}
      style={style}
      placeholder={placeholder}
    />
  );
}

// Stable text input that holds its own state and only commits on blur.
// Use this instead of <input value={...} onChange={...}/> when the parent
// re-renders frequently and the input would otherwise lose focus / drop characters.
function TextInput({ value, onCommit, style, placeholder, type = "text" }) {
  const [text, setText] = useState(value || "");
  useEffect(() => {
    setText(value || "");
  }, [value]);
  return (
    <input
      type={type}
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={() => onCommit(text)}
      style={style}
      placeholder={placeholder}
    />
  );
}

export default function OutboundAgentPage({ isMobile }) {
  const auth = useAuth();
  const toast = useToast();
  const orgId = auth.currentUser?._orgId || window.__coreflow_org_id;

  const [tab, setTab] = useState("dashboard");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dashboard data
  const [sendQueue, setSendQueue] = useState([]);
  const [domainTracking, setDomainTracking] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [recentSends, setRecentSends] = useState([]);

  // Dashboard filters
  const [filterDomain, setFilterDomain] = useState("all");
  const [filterVertical, setFilterVertical] = useState("all");
  const [filterDateRange, setFilterDateRange] = useState("today");
  const [filterStatus, setFilterStatus] = useState("all");

  // Edit states
  const [editingSequence, setEditingSequence] = useState(null);
  const [editingFilter, setEditingFilter] = useState(null);
  const [localConfig, setLocalConfig] = useState(null);
  const [verifiedDomains, setVerifiedDomains] = useState([]);

  // Unibox state
  const [agentReplies, setAgentReplies] = useState([]);
  const [selectedReply, setSelectedReply] = useState(null);
  const [replyFilter, setReplyFilter] = useState("all");
  const [replyText, setReplyText] = useState("");

  // Snov lists state
  const [snovLists, setSnovLists] = useState([]);
  const [snovListsLoading, setSnovListsLoading] = useState(false);
  const [snovListsError, setSnovListsError] = useState(null);

  // Team members for owner dropdown
  const [teamMembers, setTeamMembers] = useState([]);

  // Approval queue state
  const [pendingGroups, setPendingGroups] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [expandedVertical, setExpandedVertical] = useState(null);
  const [approvalBusy, setApprovalBusy] = useState(false);

  // Test config state
  const [testRunning, setTestRunning] = useState(false);
  const [testResults, setTestResults] = useState(null);

  // Keep localConfig in sync when config loads from DB
  useEffect(() => { if (config) setLocalConfig({ ...config }); }, [config]);

  // User-defined verticals from config (with sensible defaults if empty)
  const verticals = (localConfig?.verticals && localConfig.verticals.length > 0)
    ? localConfig.verticals
    : [];
  const verticalLabel = (id) => verticals.find(v => v.id === id)?.label || id;
  const verticalIds = verticals.map(v => v.id);

  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "approval", label: "Approval Queue" },
    { id: "unibox", label: "Unibox" },
    { id: "verticals", label: "Verticals" },
    { id: "domains", label: "Domains" },
    { id: "templates", label: "Email Templates" },
    { id: "icp", label: "ICP Filters" },
    { id: "settings", label: "Settings" },
  ];

  /* ── Load Everything ── */
  const loadConfig = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase.from("outbound_agent_config").select("*").eq("org_id", orgId).maybeSingle();
    setConfig(data || null);
  }, [orgId]);

  const loadDashboard = useCallback(async () => {
    if (!orgId) return;
    const dateFilter = getDateFilter(filterDateRange);

    // Send queue stats
    let queueQuery = supabase.from("outbound_send_queue").select("id, status, send_domain, send_inbox, to_email, subject, scheduled_for, sent_at, sequence_id, error_message, created_at").eq("org_id", orgId);
    if (dateFilter) queueQuery = queueQuery.gte("created_at", dateFilter);
    if (filterDomain !== "all") queueQuery = queueQuery.eq("send_domain", filterDomain);
    if (filterStatus !== "all") queueQuery = queueQuery.eq("status", filterStatus);
    const { data: queue } = await queueQuery.order("created_at", { ascending: false }).limit(500);
    setSendQueue(queue || []);

    // Domain tracking
    let trackQuery = supabase.from("outbound_domain_tracking").select("*").eq("org_id", orgId);
    if (dateFilter) trackQuery = trackQuery.gte("send_date", dateFilter.split("T")[0]);
    if (filterDomain !== "all") trackQuery = trackQuery.eq("domain", filterDomain);
    const { data: tracking } = await trackQuery.order("send_date", { ascending: false });
    setDomainTracking(tracking || []);

    // Recent sends for the feed
    const { data: recent } = await supabase.from("outbound_send_queue")
      .select("id, to_email, to_name, subject, status, send_domain, send_inbox, sent_at, error_message, created_at")
      .eq("org_id", orgId).eq("status", "sent")
      .order("sent_at", { ascending: false }).limit(50);
    setRecentSends(recent || []);
  }, [orgId, filterDomain, filterDateRange, filterStatus]);

  const loadSequences = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase.from("outbound_sequences").select("*").eq("org_id", orgId).order("created_at");
    setSequences(data || []);
  }, [orgId]);

  const loadVerifiedDomains = useCallback(async () => {
    try {
      const resp = await apiFetch("/api/email/domains", {
        method: "POST",
        body: JSON.stringify({ action: "list", orgId }),
      });
      const data = await resp.json();
      if (data.domains) setVerifiedDomains(data.domains.filter(d => d.status === "verified"));
    } catch (e) { console.warn("[Outbound] Failed to load verified domains:", e.message); }
  }, [orgId]);

  const loadAgentReplies = useCallback(async () => {
    if (!orgId) return;
    // Get contacts that were sourced by the agent and have replies
    const { data: sentEmails } = await supabase.from("outbound_send_queue")
      .select("contact_id, to_email, to_name, subject, sent_at, send_inbox, sequence_id")
      .eq("org_id", orgId).eq("status", "sent")
      .order("sent_at", { ascending: false }).limit(500);

    if (!sentEmails || sentEmails.length === 0) { setAgentReplies([]); return; }

    const contactIds = [...new Set(sentEmails.map(e => e.contact_id).filter(Boolean))];
    if (contactIds.length === 0) { setAgentReplies([]); return; }

    // Get all inbound emails from these contacts
    const { data: replies } = await supabase.from("emails")
      .select("id, contact_id, from_address, to_address, subject, body_text, status, agent_classified, created_at")
      .eq("org_id", orgId).eq("direction", "inbound")
      .in("contact_id", contactIds.slice(0, 200))
      .order("created_at", { ascending: false });

    // Get contact details
    const { data: contacts } = await supabase.from("contacts")
      .select("id, first_name, last_name, email, company, job_title, owner_id, score, properties")
      .in("id", contactIds.slice(0, 200));

    const contactMap = {};
    (contacts || []).forEach(c => { contactMap[c.id] = c; });

    const sentMap = {};
    sentEmails.forEach(e => { if (e.contact_id && !sentMap[e.contact_id]) sentMap[e.contact_id] = e; });

    const enriched = (replies || []).map(r => ({
      ...r,
      contact: contactMap[r.contact_id] || null,
      originalSend: sentMap[r.contact_id] || null,
    }));

    setAgentReplies(enriched);
  }, [orgId]);

  // Load Snov lists for the dropdown
  const loadSnovLists = useCallback(async () => {
    if (!orgId) return;
    setSnovListsLoading(true);
    setSnovListsError(null);
    try {
      const resp = await fetch("/api/snov/list-lists?orgId=" + encodeURIComponent(orgId));
      const data = await resp.json();
      if (data.success && data.lists) {
        setSnovLists(data.lists);
      } else {
        setSnovListsError(data.error || "Failed to load lists");
      }
    } catch (e) {
      setSnovListsError(e.message);
    } finally {
      setSnovListsLoading(false);
    }
  }, [orgId]);

  // Run end-to-end config test
  const runConfigTest = useCallback(async () => {
    if (!orgId) return;
    setTestRunning(true);
    setTestResults(null);
    try {
      const resp = await fetch("/api/snov/test-config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId }),
      });
      const data = await resp.json();
      setTestResults(data);
    } catch (e) {
      setTestResults({ success: false, error: e.message, checks: [] });
    } finally {
      setTestRunning(false);
    }
  }, [orgId]);

  // Load team members for the Owner dropdown on filters
  const loadTeam = useCallback(async () => {
    if (!orgId) return;
    try {
      const resp = await fetch(`/api/team/list?orgId=${orgId}`);
      const data = await resp.json();
      if (data.success && Array.isArray(data.users)) {
        setTeamMembers(data.users);
      }
    } catch (e) {
      console.warn("[OutboundAgent] Failed to load team:", e.message);
    }
  }, [orgId]);

  // Load pending-approval emails grouped by vertical
  const loadPending = useCallback(async () => {
    if (!orgId) return;
    setPendingLoading(true);
    try {
      const resp = await fetch(`/api/outbound/pending?orgId=${orgId}`);
      const data = await resp.json();
      if (data.success) {
        setPendingGroups(data.groups || []);
        setPendingTotal(data.total || 0);
      }
    } catch (e) {
      console.warn("[OutboundAgent] Failed to load pending:", e.message);
    } finally {
      setPendingLoading(false);
    }
  }, [orgId]);

  // Approve or reject emails (by ids, vertical, or all)
  const handleApproval = useCallback(async (action, payload) => {
    if (!orgId) return;
    setApprovalBusy(true);
    try {
      const resp = await fetch("/api/outbound/approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, orgId, ...payload }),
      });
      const data = await resp.json();
      if (data.success) {
        await loadPending(); // Refresh
        await loadDashboard();
      } else {
        console.error("[OutboundAgentPage] Approval failed:", data.error);
        toast?.error?.("Failed to process approval: " + (data.error || "unknown error"));
      }
    } catch (e) {
      console.error("[OutboundAgentPage] Approval exception:", e);
      toast?.error?.("Failed to process approval: " + (e.message || "unknown error"));
    } finally {
      setApprovalBusy(false);
    }
  }, [orgId, loadPending, loadDashboard, toast]);

  useEffect(() => {
    Promise.all([loadConfig(), loadDashboard(), loadSequences(), loadVerifiedDomains(), loadAgentReplies(), loadTeam(), loadPending()]).finally(() => setLoading(false));
  }, [loadConfig, loadDashboard, loadSequences, loadVerifiedDomains, loadAgentReplies, loadTeam, loadPending]);

  // Load Snov lists once after config loads (so we have credentials available)
  useEffect(() => {
    if (!loading && config?.snov_client_id && config?.snov_client_secret) {
      loadSnovLists();
    }
  }, [loading, config?.snov_client_id, config?.snov_client_secret, loadSnovLists]);

  useEffect(() => { if (!loading) loadDashboard(); }, [filterDomain, filterDateRange, filterStatus]);
  useEffect(() => { if (!loading && tab === "approval") loadPending(); }, [tab]);

  /* ── Helpers ── */
  function getDateFilter(range) {
    const now = new Date();
    switch (range) {
      case "today": return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      case "7d": return new Date(now - 7 * 86400000).toISOString();
      case "30d": return new Date(now - 30 * 86400000).toISOString();
      case "all": return null;
      default: return null;
    }
  }

  /* ── Config Updates ── */
  const updateConfig = async (field, value) => {
    if (!config) {
      const { data } = await supabase.from("outbound_agent_config").insert({ org_id: orgId, [field]: value }).select().single();
      setConfig(data);
      setLocalConfig(data);
    } else {
      await supabase.from("outbound_agent_config").update({ [field]: value, updated_at: new Date().toISOString() }).eq("id", config.id);
      setConfig(prev => ({ ...prev, [field]: value }));
      // Also sync localConfig so that subsequent flushConfig calls don't overwrite with stale data
      setLocalConfig(prev => prev ? { ...prev, [field]: value } : prev);
    }
  };

  // Update local state only (for text inputs while typing)
  const updateLocal = (field, value) => {
    setLocalConfig(prev => prev ? { ...prev, [field]: value } : prev);
  };

  // Flush local state to DB (call on blur)
  const flushConfig = (field) => {
    if (localConfig && localConfig[field] !== undefined) {
      updateConfig(field, localConfig[field]);
    }
  };

  const toggleAgent = async () => {
    const newState = !(config?.enabled);
    if (newState && !config?.warmup_started_at) {
      await updateConfig("warmup_started_at", new Date().toISOString());
    }
    await updateConfig("enabled", newState);
  };

  /* ── Save Sequence ── */
  const saveSequence = async (seq) => {
    setSaving(true);
    if (seq.id) {
      await supabase.from("outbound_sequences").update({ name: seq.name, vertical: seq.vertical, steps: seq.steps, is_active: seq.is_active, updated_at: new Date().toISOString() }).eq("id", seq.id);
    } else {
      await supabase.from("outbound_sequences").insert({ org_id: orgId, name: seq.name, vertical: seq.vertical, steps: seq.steps, is_active: true });
    }
    await loadSequences();
    setEditingSequence(null);
    setSaving(false);
  };

  /* ── Save ICP Filter ── */
  const saveFilters = async (filters) => {
    await updateConfig("icp_filters", filters);
  };

  /* ── Styles ── */
  const cardS = { padding: 20, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8, backdropFilter: "none" };
  const inputS = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.04)", color: T.txt, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
  const labelS = { fontSize: 11, color: T.tx3, marginBottom: 6, display: "block", letterSpacing: "0.04em" };
  const sectionLabel = { fontSize: 11, fontWeight: 400, color: T.tx3, letterSpacing: ".08em", marginBottom: 14 };
  const selectS = { ...inputS, cursor: "pointer" };
  const miniCard = (color) => ({ textAlign: "center", padding: "16px 12px", borderRadius: 8, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)", flex: 1, minWidth: 100 });

  // Compute dashboard stats
  const totalSent = sendQueue.filter(e => e.status === "sent").length;
  const totalQueued = sendQueue.filter(e => e.status === "queued").length;
  const totalFailed = sendQueue.filter(e => e.status === "failed").length;
  const totalEmails = sendQueue.length;

  // Domain list from config
  const allDomains = (config?.sending_domains || []).map(d => d.domain);

  // Domain health aggregation
  const domainStats = {};
  for (const t of domainTracking) {
    if (!domainStats[t.domain]) domainStats[t.domain] = { sent: 0, failed: 0, bounced: 0, inboxes: new Set() };
    domainStats[t.domain].sent += t.sent_count || 0;
    domainStats[t.domain].failed += t.failed_count || 0;
    domainStats[t.domain].bounced += t.bounce_count || 0;
    domainStats[t.domain].inboxes.add(t.inbox);
  }

  if (loading) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.tx3 }}>Loading The Prospector...</div>;
  }

  return (
    <div style={{ padding: isMobile ? 16 : 24, maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 300, color: T.txt, margin: 0, letterSpacing: "-0.01em" }}>The Prospector</h1>
          <p style={{ fontSize: 12, color: T.tx3, margin: "6px 0 0" }}>Autonomous lead sourcing and cold outreach</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {config?.warmup_started_at && (
            <span style={{ fontSize: 10, color: T.tx3 }}>
              Warmup day {Math.floor((Date.now() - new Date(config.warmup_started_at).getTime()) / 86400000) + 1}
            </span>
          )}
          <button onClick={runConfigTest} disabled={testRunning} style={{
            padding: "10px 18px", borderRadius: 8, fontFamily: "inherit",
            background: "rgba(64,89,173,0.1)", color: T.blue, fontSize: 12, cursor: testRunning ? "wait" : "pointer",
            border: "1px solid rgba(64,89,173,0.3)",
          }}>
            {testRunning ? "Testing..." : "Test Configuration"}
          </button>
          <button onClick={toggleAgent} style={{
            padding: "10px 24px", borderRadius: 8, border: "none", fontFamily: "inherit",
            background: config?.enabled ? T.red : T.grn, color: "#fff", fontSize: 13, fontWeight: 400, cursor: "pointer",
          }}>
            {config?.enabled ? "Pause Agent" : "Start Agent"}
          </button>
        </div>
      </div>

      {/* Test Configuration results panel */}
      {testResults && (
        <div style={{
          marginBottom: 20, padding: 16, borderRadius: 8,
          background: testResults.ready ? "rgba(34,197,94,0.06)" : "rgba(245,158,11,0.06)",
          border: "1px solid " + (testResults.ready ? "rgba(34,197,94,0.25)" : "rgba(245,158,11,0.25)"),
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: testResults.ready ? T.grn : (T.amber || "#f59e0b"), fontWeight: 500 }}>
              {testResults.summary || (testResults.error ? "Test failed: " + testResults.error : "Test complete")}
            </div>
            <button onClick={() => setTestResults(null)} style={{
              background: "none", border: "none", color: T.tx3, fontSize: 16, cursor: "pointer", padding: 4,
            }}>×</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(testResults.checks || []).map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 10, fontSize: 11, alignItems: "flex-start" }}>
                <span style={{ color: c.ok ? T.grn : T.red, width: 14, flexShrink: 0, fontWeight: 500 }}>{c.ok ? "✓" : "×"}</span>
                <span style={{ color: T.txt, minWidth: 200, flexShrink: 0 }}>{c.name}</span>
                <span style={{ color: T.tx3 }}>{c.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, marginBottom: 24, borderBottom: "1px solid " + T.bdr }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "10px 18px", fontSize: 12, fontWeight: 400, fontFamily: "inherit",
            color: tab === t.id ? T.txt : T.tx3, background: "none", border: "none",
            borderBottom: tab === t.id ? "2px solid " + T.blue : "2px solid transparent",
            cursor: "pointer", marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ══════════════════════════════════════
         DASHBOARD TAB
         ══════════════════════════════════════ */}
      {tab === "dashboard" && (
        <div>
          {/* Filters */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            <select value={filterDateRange} onChange={e => setFilterDateRange(e.target.value)} style={{ ...selectS, width: "auto", minWidth: 120 }}>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="all">All time</option>
            </select>
            <select value={filterDomain} onChange={e => setFilterDomain(e.target.value)} style={{ ...selectS, width: "auto", minWidth: 160 }}>
              <option value="all">All domains</option>
              {allDomains.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...selectS, width: "auto", minWidth: 120 }}>
              <option value="all">All statuses</option>
              <option value="queued">Queued</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
            <select value={filterVertical} onChange={e => setFilterVertical(e.target.value)} style={{ ...selectS, width: "auto", minWidth: 160 }}>
              <option value="all">All verticals</option>
              {verticals.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </div>

          {/* KPI Cards */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={miniCard(T.blue)}>
              <div style={{ fontSize: 26, fontWeight: 300, color: T.blue }}>{totalEmails.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>Total Emails</div>
            </div>
            <div style={miniCard(T.grn)}>
              <div style={{ fontSize: 26, fontWeight: 300, color: T.grn }}>{totalSent.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>Sent</div>
            </div>
            <div style={miniCard(T.amb)}>
              <div style={{ fontSize: 26, fontWeight: 300, color: T.amb }}>{totalQueued.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>Queued</div>
            </div>
            <div style={miniCard(T.red)}>
              <div style={{ fontSize: 26, fontWeight: 300, color: T.red }}>{totalFailed.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>Failed</div>
            </div>
            <div style={miniCard(T.pur)}>
              <div style={{ fontSize: 26, fontWeight: 300, color: T.pur }}>{config?.total_leads_sourced?.toLocaleString() || 0}</div>
              <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>Leads Sourced</div>
            </div>
            <div style={miniCard(T.cyn)}>
              <div style={{ fontSize: 26, fontWeight: 300, color: T.cyn }}>{config?.total_replies?.toLocaleString() || 0}</div>
              <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>Replies</div>
            </div>
          </div>

          {/* Domain Health Grid */}
          <div style={sectionLabel}>DOMAIN HEALTH</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, marginBottom: 24 }}>
            {allDomains.map(domain => {
              const stats = domainStats[domain] || { sent: 0, failed: 0, bounced: 0, inboxes: new Set() };
              const health = stats.bounced > 5 ? "warning" : stats.failed > 10 ? "danger" : "healthy";
              const healthColor = health === "healthy" ? T.grn : health === "warning" ? T.amb : T.red;
              const inboxCount = (config?.sending_domains || []).find(d => d.domain === domain)?.inboxes?.length || 0;
              const maxDaily = inboxCount * (config?.max_emails_per_inbox || 20);
              const todayTracking = domainTracking.filter(t => t.domain === domain && t.send_date === new Date().toISOString().split("T")[0]);
              const todaySent = todayTracking.reduce((sum, t) => sum + (t.sent_count || 0), 0);

              return (
                <div key={domain} style={cardS}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{domain}</div>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: healthColor }} />
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.tx3, marginBottom: 4 }}>
                      <span>Today: {todaySent} / {maxDaily}</span>
                      <span>{Math.round((todaySent / Math.max(maxDaily, 1)) * 100)}%</span>
                    </div>
                    <ProgressBar value={todaySent} max={maxDaily} color={healthColor} height={4} />
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 10 }}>
                    <span style={{ color: T.grn }}>{stats.sent} sent</span>
                    <span style={{ color: T.red }}>{stats.failed} failed</span>
                    <span style={{ color: T.amb }}>{stats.bounced} bounced</span>
                    <span style={{ color: T.tx3 }}>{inboxCount} inboxes</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Per-Inbox Breakdown */}
          <div style={sectionLabel}>INBOX BREAKDOWN — TODAY</div>
          <div style={{ ...cardS, marginBottom: 24 }}>
            {domainTracking.filter(t => t.send_date === new Date().toISOString().split("T")[0]).length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: T.tx3, fontSize: 12 }}>No sends today yet</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid " + T.bdr, color: T.tx3, fontWeight: 400 }}>Inbox</th>
                      <th style={{ textAlign: "center", padding: "8px 10px", borderBottom: "1px solid " + T.bdr, color: T.tx3, fontWeight: 400 }}>Sent</th>
                      <th style={{ textAlign: "center", padding: "8px 10px", borderBottom: "1px solid " + T.bdr, color: T.tx3, fontWeight: 400 }}>Limit</th>
                      <th style={{ textAlign: "center", padding: "8px 10px", borderBottom: "1px solid " + T.bdr, color: T.tx3, fontWeight: 400 }}>Remaining</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid " + T.bdr, color: T.tx3, fontWeight: 400 }}>Capacity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domainTracking.filter(t => t.send_date === new Date().toISOString().split("T")[0]).map(t => (
                      <tr key={t.id}>
                        <td style={{ padding: "10px 10px", color: T.txt, borderBottom: "1px solid " + T.bdr }}>{t.inbox}</td>
                        <td style={{ textAlign: "center", padding: "10px", color: T.grn, borderBottom: "1px solid " + T.bdr }}>{t.sent_count}</td>
                        <td style={{ textAlign: "center", padding: "10px", color: T.tx3, borderBottom: "1px solid " + T.bdr }}>{t.max_allowed}</td>
                        <td style={{ textAlign: "center", padding: "10px", color: T.amb, borderBottom: "1px solid " + T.bdr }}>{Math.max(0, (t.max_allowed || 20) - (t.sent_count || 0))}</td>
                        <td style={{ padding: "10px", borderBottom: "1px solid " + T.bdr, width: 120 }}>
                          <ProgressBar value={t.sent_count || 0} max={t.max_allowed || 20} color={t.sent_count >= t.max_allowed ? T.red : T.grn} height={4} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Activity Feed */}
          <div style={sectionLabel}>RECENT SENDS</div>
          <div style={cardS}>
            {recentSends.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: T.tx3, fontSize: 12 }}>No sends yet</div>
            ) : (
              <div style={{ maxHeight: 400, overflow: "auto" }}>
                {recentSends.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid " + T.bdr }}>
                    <div style={{ width: 6, height: 6, borderRadius: 3, background: s.status === "sent" ? T.grn : s.status === "failed" ? T.red : T.amb, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: T.txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.to_name || s.to_email} — {s.subject}
                      </div>
                      <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>
                        {s.send_inbox || "pending"} {s.sent_at ? " — " + cfDate(s.sent_at) : ""}
                      </div>
                    </div>
                    <Badge>{s.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Send Heatmap */}
          <div style={{ marginTop: 20 }}>
            <div style={sectionLabel}>SEND HEATMAP</div>
            <div style={cardS}>
              {(() => {
                const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                const hours = Array.from({ length: 12 }, (_, i) => i + 7);
                const heatData = {};
                days.forEach(d => { hours.forEach(h => { heatData[d + "-" + h] = 0; }); });
                sendQueue.filter(e => e.status === "sent" && e.sent_at).forEach(e => {
                  const dt = new Date(e.sent_at);
                  const day = days[dt.getDay() === 0 ? 6 : dt.getDay() - 1];
                  const hour = dt.getHours();
                  if (hour >= 7 && hour <= 18) { const key = day + "-" + hour; if (heatData[key] !== undefined) heatData[key]++; }
                });
                const maxSent = Math.max(1, ...Object.values(heatData));
                return (
                  <div style={{ overflowX: "auto" }}>
                    <div style={{ display: "flex", gap: 2 }}>
                      <div style={{ width: 36, flexShrink: 0 }} />
                      {hours.map(h => <div key={h} style={{ flex: 1, minWidth: 32, textAlign: "center", fontSize: 9, color: T.tx3, paddingBottom: 4 }}>{h > 12 ? (h-12)+"p" : h+"a"}</div>)}
                    </div>
                    {days.map(day => (
                      <div key={day} style={{ display: "flex", gap: 2, marginBottom: 2 }}>
                        <div style={{ width: 36, fontSize: 10, color: T.tx3, display: "flex", alignItems: "center", flexShrink: 0 }}>{day}</div>
                        {hours.map(h => {
                          const v = heatData[day + "-" + h];
                          const intensity = v / maxSent;
                          const bg = intensity === 0 ? "rgba(255,255,255,0.03)" : intensity < 0.25 ? "rgba(64,89,173,0.15)" : intensity < 0.5 ? "rgba(64,89,173,0.3)" : intensity < 0.75 ? "rgba(64,89,173,0.5)" : "rgba(64,89,173,0.7)";
                          return <div key={h} title={day+" "+h+":00 — "+v+" sent"} style={{ flex: 1, minWidth: 32, height: 24, borderRadius: 3, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: intensity > 0.5 ? "#fff" : T.tx3 }}>{v > 0 ? v : ""}</div>;
                        })}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Performance by Vertical */}
          <div style={{ marginTop: 20 }}>
            <div style={sectionLabel}>PERFORMANCE BY VERTICAL</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
              {(() => {
                const vertStats = {};
                sendQueue.forEach(e => {
                  const seq = sequences.find(s => s.id === e.sequence_id);
                  const v = seq?.vertical || "unknown";
                  if (!vertStats[v]) vertStats[v] = { sent: 0, replied: 0 };
                  if (e.status === "sent") vertStats[v].sent++;
                });
                agentReplies.forEach(r => {
                  const v = r.contact?.properties?.vertical || "unknown";
                  if (!vertStats[v]) vertStats[v] = { sent: 0, replied: 0 };
                  vertStats[v].replied++;
                });
                return Object.entries(vertStats).map(([v, stats]) => {
                  const rate = stats.sent > 0 ? Math.round((stats.replied / stats.sent) * 100) : 0;
                  return (
                    <div key={v} style={cardS}>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 8 }}>{verticalLabel(v)}</div>
                      <div style={{ display: "flex", gap: 16, fontSize: 11 }}>
                        <div><span style={{ color: T.grn, fontWeight: 400 }}>{stats.sent}</span> <span style={{ color: T.tx3 }}>sent</span></div>
                        <div><span style={{ color: T.blue, fontWeight: 400 }}>{stats.replied}</span> <span style={{ color: T.tx3 }}>replied</span></div>
                        <div><span style={{ color: T.amb, fontWeight: 400 }}>{rate}%</span> <span style={{ color: T.tx3 }}>rate</span></div>
                      </div>
                      <div style={{ marginTop: 8 }}><ProgressBar value={rate} max={100} color={rate >= 10 ? T.grn : rate >= 5 ? T.amb : T.red} height={3} /></div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Revenue Attribution */}
          <div style={{ marginTop: 20 }}>
            <div style={sectionLabel}>REVENUE ATTRIBUTION</div>
            <div style={cardS}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 300, color: T.txt }}>{[...new Set(sendQueue.filter(e => e.status === "sent").map(e => e.contact_id).filter(Boolean))].length}</div>
                  <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>Leads Sourced</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 300, color: T.txt }}>{totalSent}</div>
                  <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>Emails Sent</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 300, color: T.blue }}>{agentReplies.filter(r => r.agent_classified === "interested").length}</div>
                  <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>Interested</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 300, color: T.grn }}>{agentReplies.length}</div>
                  <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>Total Replies</div>
                </div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.06)", fontSize: 11, color: T.tx3 }}>
                Revenue attribution tracks from agent-sourced lead to closed deal. As deals close from agent contacts, the full ROI calculates automatically.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
         APPROVAL QUEUE TAB
         ══════════════════════════════════════ */}
      {tab === "approval" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 500, margin: 0, marginBottom: 4, color: T.txt }}>Approval Queue</h2>
              <div style={{ fontSize: 12, color: T.tx3 }}>
                {pendingTotal > 0
                  ? <>Review <strong style={{ color: T.txt }}>{pendingTotal}</strong> pending email{pendingTotal !== 1 ? "s" : ""} before they go out</>
                  : "No pending emails. New emails will appear here after the agent sources them."}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={loadPending} disabled={pendingLoading} style={{
                padding: "8px 14px", borderRadius: 8, fontSize: 11, fontFamily: "inherit", cursor: "pointer",
                border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.04)", color: T.txt,
                opacity: pendingLoading ? 0.5 : 1,
              }}>{pendingLoading ? "Loading..." : "↻ Refresh"}</button>
              {pendingTotal > 0 && (
                <>
                  <button
                    disabled={approvalBusy}
                    onClick={() => {
                      if (window.confirm(`Reject all ${pendingTotal} pending emails? They will be marked skipped and won't send.`)) {
                        handleApproval("reject", { all: true });
                      }
                    }}
                    style={{
                      padding: "8px 14px", borderRadius: 8, fontSize: 11, fontFamily: "inherit", cursor: "pointer",
                      border: "1px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.08)", color: T.red,
                      opacity: approvalBusy ? 0.5 : 1,
                    }}>Reject All</button>
                  <button
                    disabled={approvalBusy}
                    onClick={() => {
                      if (window.confirm(`Approve all ${pendingTotal} pending emails? They will start sending immediately.`)) {
                        handleApproval("approve", { all: true });
                      }
                    }}
                    style={{
                      padding: "8px 14px", borderRadius: 8, fontSize: 11, fontFamily: "inherit", cursor: "pointer",
                      border: "1px solid rgba(34,197,94,0.3)", background: "rgba(34,197,94,0.12)", color: "#22c55e",
                      opacity: approvalBusy ? 0.5 : 1, fontWeight: 500,
                    }}>Approve All</button>
                </>
              )}
            </div>
          </div>

          {pendingGroups.length === 0 && !pendingLoading && (
            <div style={{ ...cardS, padding: 40, textAlign: "center", color: T.tx3, fontSize: 13 }}>
              No pending emails right now. When the agent sources new leads with approval mode on, they'll show up here for you to review before sending.
            </div>
          )}

          {/* Group cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {pendingGroups.map(group => {
              const verticalLabel = verticals.find(v => v.id === group.vertical)?.label || group.vertical;
              const isExpanded = expandedVertical === group.vertical;
              // Show 3 random samples if collapsed, all if expanded
              const samples = isExpanded ? group.emails : group.emails.slice(0, 3);
              return (
                <div key={group.vertical} style={{ ...cardS, padding: 0, overflow: "hidden" }}>
                  {/* Header */}
                  <div style={{
                    padding: "14px 18px", borderBottom: "1px solid rgba(217,219,221,0.08)",
                    display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
                    background: "rgba(255,255,255,0.02)",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: T.txt }}>{verticalLabel}</div>
                      <div style={{ fontSize: 11, color: T.tx3 }}>
                        {group.count} email{group.count !== 1 ? "s" : ""}
                      </div>
                      {group.has_unmerged && (
                        <div style={{
                          padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 500,
                          background: "rgba(239,68,68,0.12)", color: T.red, border: "1px solid rgba(239,68,68,0.25)",
                        }}>⚠ UNMERGED PLACEHOLDER DETECTED</div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        onClick={() => setExpandedVertical(isExpanded ? null : group.vertical)}
                        style={{
                          padding: "6px 12px", borderRadius: 6, fontSize: 10, fontFamily: "inherit", cursor: "pointer",
                          border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.03)", color: T.txt,
                        }}>{isExpanded ? "Collapse" : `Show all ${group.count}`}</button>
                      <button
                        disabled={approvalBusy}
                        onClick={() => {
                          if (window.confirm(`Reject all ${group.count} ${verticalLabel} emails?`)) {
                            handleApproval("reject", { vertical: group.vertical });
                          }
                        }}
                        style={{
                          padding: "6px 12px", borderRadius: 6, fontSize: 10, fontFamily: "inherit", cursor: "pointer",
                          border: "1px solid rgba(239,68,68,0.2)", background: "rgba(239,68,68,0.06)", color: T.red,
                        }}>Reject All</button>
                      <button
                        disabled={approvalBusy}
                        onClick={() => handleApproval("approve", { vertical: group.vertical })}
                        style={{
                          padding: "6px 12px", borderRadius: 6, fontSize: 10, fontFamily: "inherit", cursor: "pointer",
                          border: "1px solid rgba(34,197,94,0.3)", background: "rgba(34,197,94,0.12)", color: "#22c55e", fontWeight: 500,
                        }}>Approve All</button>
                    </div>
                  </div>

                  {/* Sample emails */}
                  <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                    {!isExpanded && group.count > 3 && (
                      <div style={{ fontSize: 10, color: T.tx3, fontStyle: "italic", marginBottom: 4 }}>
                        Showing 3 of {group.count} random samples. Click "Show all" to review each one.
                      </div>
                    )}
                    {samples.map(email => {
                      const hasUnmerged = /\{\{[^}]+\}\}/.test((email.subject || "") + " " + (email.body_text || ""));
                      return (
                        <div key={email.id} style={{
                          padding: 14, borderRadius: 8,
                          background: hasUnmerged ? "rgba(239,68,68,0.04)" : "rgba(255,255,255,0.02)",
                          border: hasUnmerged ? "1px solid rgba(239,68,68,0.25)" : "1px solid rgba(217,219,221,0.08)",
                        }}>
                          {/* Meta row */}
                          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 14px", fontSize: 10, color: T.tx3, marginBottom: 10 }}>
                            <div>From:</div>
                            <div style={{ color: T.txt }}>{email.from_name} &lt;{email.from_email || "(rotating)"}{email.from_email?.endsWith("@") ? "<domain>" : ""}&gt;</div>
                            <div>Reply-to:</div>
                            <div style={{ color: T.txt }}>{email.reply_to || "—"}</div>
                            <div>To:</div>
                            <div style={{ color: T.txt }}>
                              {email.to_name} &lt;{email.to_email}&gt;
                              {email.contacts?.company && <span style={{ color: T.tx3 }}> · {email.contacts.company}</span>}
                              {email.contacts?.job_title && <span style={{ color: T.tx3 }}> · {email.contacts.job_title}</span>}
                            </div>
                          </div>
                          {/* Subject */}
                          <div style={{ fontSize: 13, fontWeight: 500, color: T.txt, marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
                            {email.subject}
                          </div>
                          {/* Body */}
                          <div style={{ fontSize: 12, color: T.txt, whiteSpace: "pre-wrap", lineHeight: 1.5, fontFamily: "inherit" }}>
                            {email.body_text}
                          </div>
                          {/* Per-email actions */}
                          <div style={{ display: "flex", gap: 6, marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
                            <button
                              disabled={approvalBusy}
                              onClick={() => handleApproval("reject", { ids: [email.id] })}
                              style={{
                                padding: "4px 10px", borderRadius: 4, fontSize: 10, fontFamily: "inherit", cursor: "pointer",
                                border: "1px solid rgba(239,68,68,0.2)", background: "transparent", color: T.red,
                              }}>Reject</button>
                            <button
                              disabled={approvalBusy}
                              onClick={() => handleApproval("approve", { ids: [email.id] })}
                              style={{
                                padding: "4px 10px", borderRadius: 4, fontSize: 10, fontFamily: "inherit", cursor: "pointer",
                                border: "1px solid rgba(34,197,94,0.3)", background: "transparent", color: "#22c55e",
                              }}>Approve</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
         UNIBOX TAB — All agent replies in one view
         ══════════════════════════════════════ */}
      {tab === "unibox" && (
        <div style={{ display: "flex", gap: 0, height: "calc(100vh - 200px)", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8, overflow: "hidden" }}>
          {/* Reply List */}
          <div style={{ width: 380, borderRight: "1px solid rgba(217,219,221,0.06)", overflow: "auto", background: "#2C2E33" }}>
            {/* Filters */}
            <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(217,219,221,0.06)", display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["all", "interested", "not_now", "unsubscribe", "unclassified"].map(f => (
                <button key={f} onClick={() => setReplyFilter(f)} style={{
                  padding: "4px 10px", borderRadius: 6, fontSize: 10, fontFamily: "inherit", cursor: "pointer",
                  border: replyFilter === f ? "1px solid rgba(64,89,173,0.3)" : "1px solid rgba(217,219,221,0.08)",
                  background: replyFilter === f ? "rgba(64,89,173,0.12)" : "rgba(255,255,255,0.04)",
                  color: replyFilter === f ? T.blue : T.tx3,
                }}>{f === "all" ? "All" : f === "not_now" ? "Not Now" : f === "unclassified" ? "New" : f.charAt(0).toUpperCase() + f.slice(1)}</button>
              ))}
            </div>
            {/* Reply items */}
            {(() => {
              const filtered = agentReplies.filter(r => {
                if (replyFilter === "all") return true;
                if (replyFilter === "unclassified") return !r.agent_classified;
                return r.agent_classified === replyFilter;
              });
              if (filtered.length === 0) return <div style={{ padding: 32, textAlign: "center", color: T.tx3, fontSize: 12 }}>No replies yet</div>;
              return filtered.map(r => {
                const c = r.contact;
                const classColor = r.agent_classified === "interested" ? T.grn : r.agent_classified === "not_now" ? T.amb : r.agent_classified === "unsubscribe" ? T.red : T.tx3;
                const isSelected = selectedReply?.id === r.id;
                return (
                  <div key={r.id} onClick={() => { setSelectedReply(r); setReplyText(""); }} style={{
                    padding: "14px 16px", cursor: "pointer", borderBottom: "1px solid rgba(217,219,221,0.04)",
                    background: isSelected ? "rgba(64,89,173,0.12)" : "transparent",
                  }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: T.txt, flexShrink: 0 }}>
                        {c ? (c.first_name?.[0] || "") + (c.last_name?.[0] || "") : "?"}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c ? ((c.first_name || "") + " " + (c.last_name || "")).trim() : r.from_address}
                        </div>
                        <div style={{ fontSize: 10, color: T.tx3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c?.company || ""}</div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                        <span style={{ fontSize: 9, color: T.tx3 }}>{cfDate(r.created_at)}</span>
                        {r.agent_classified && <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: classColor + "15", color: classColor }}>{r.agent_classified}</span>}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: T.tx3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(r.body_text || r.subject || "").slice(0, 100)}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Reply Detail */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#25272B" }}>
            {!selectedReply ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.tx3, fontSize: 13 }}>Select a reply to view</div>
            ) : (
              <>
                {/* Header */}
                <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(217,219,221,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>
                      {selectedReply.contact ? ((selectedReply.contact.first_name || "") + " " + (selectedReply.contact.last_name || "")).trim() : selectedReply.from_address}
                    </div>
                    <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>
                      {selectedReply.contact?.job_title ? selectedReply.contact.job_title + " at " : ""}{selectedReply.contact?.company || selectedReply.from_address}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["interested", "not_now", "unsubscribe"].map(tag => (
                      <button key={tag} onClick={async () => {
                        await supabase.from("emails").update({ agent_classified: tag }).eq("id", selectedReply.id);
                        if (tag === "interested" && selectedReply.contact_id) {
                          await supabase.from("contacts").update({ status: "active", score: Math.min((selectedReply.contact?.score || 30) + 30, 100) }).eq("id", selectedReply.contact_id);
                        }
                        if (tag === "unsubscribe" && selectedReply.contact_id) {
                          await supabase.from("contacts").update({ status: "inactive" }).eq("id", selectedReply.contact_id);
                          await supabase.from("outbound_send_queue").update({ status: "skipped" }).eq("org_id", orgId).eq("contact_id", selectedReply.contact_id).eq("status", "queued");
                        }
                        setSelectedReply(prev => ({ ...prev, agent_classified: tag }));
                        loadAgentReplies();
                      }} style={{
                        padding: "5px 12px", borderRadius: 6, fontSize: 10, fontFamily: "inherit", cursor: "pointer",
                        border: selectedReply.agent_classified === tag ? "1px solid " + (tag === "interested" ? T.grn : tag === "not_now" ? T.amb : T.red) + "40" : "1px solid rgba(217,219,221,0.12)",
                        background: selectedReply.agent_classified === tag ? (tag === "interested" ? T.grn : tag === "not_now" ? T.amb : T.red) + "15" : "rgba(255,255,255,0.04)",
                        color: tag === "interested" ? T.grn : tag === "not_now" ? T.amb : T.red,
                      }}>{tag === "not_now" ? "Not Now" : tag.charAt(0).toUpperCase() + tag.slice(1)}</button>
                    ))}
                  </div>
                </div>

                {/* Email body */}
                <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
                  {/* Original send */}
                  {selectedReply.originalSend && (
                    <div style={{ marginBottom: 20, padding: 14, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.06)" }}>
                      <div style={{ fontSize: 10, color: T.tx3, marginBottom: 6 }}>ORIGINAL — sent {cfDate(selectedReply.originalSend.sent_at)} via {selectedReply.originalSend.send_inbox}</div>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 4 }}>{selectedReply.originalSend.subject}</div>
                    </div>
                  )}
                  {/* Reply */}
                  <div style={{ padding: 14, borderRadius: 8, background: "rgba(64,89,173,0.06)", border: "1px solid rgba(64,89,173,0.12)" }}>
                    <div style={{ fontSize: 10, color: T.tx3, marginBottom: 6 }}>REPLY — {cfDate(selectedReply.created_at)}</div>
                    <div style={{ fontSize: 12, color: T.txt, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{selectedReply.body_text || "(no body)"}</div>
                  </div>
                </div>

                {/* Quick reply */}
                <div style={{ padding: "12px 20px", borderTop: "1px solid rgba(217,219,221,0.06)", display: "flex", gap: 8 }}>
                  <input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type a reply..."
                    onKeyDown={e => { if (e.key === "Enter" && replyText.trim()) { /* TODO: send reply via Resend */ } }}
                    style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
                  <Btn primary onClick={async () => {
                    if (!replyText.trim() || !selectedReply.contact?.email) return;
                    const fromAddr = selectedReply.originalSend?.send_inbox || "";
                    if (!fromAddr) return;
                    try {
                      await fetch("https://api.resend.com/emails", {
                        method: "POST",
                        headers: { "Authorization": "Bearer " + (window.__resend_key || ""), "Content-Type": "application/json" },
                        body: JSON.stringify({
                          from: fromAddr, to: [selectedReply.contact.email],
                          subject: "Re: " + (selectedReply.subject || ""), html: replyText.replace(/\n/g, "<br>"), text: replyText,
                        }),
                      });
                      // Log the reply
                      await supabase.from("emails").insert({
                        org_id: orgId, contact_id: selectedReply.contact_id,
                        from_address: fromAddr, to_address: selectedReply.contact.email,
                        subject: "Re: " + (selectedReply.subject || ""), body_text: replyText,
                        status: "sent", direction: "outbound",
                      });
                      setReplyText("");
                    } catch (e) { console.error("Reply failed:", e.message); }
                  }}>Send</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
         VERTICALS TAB — User-defined audiences
         ══════════════════════════════════════ */}
      {tab === "verticals" && (
        <div>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 400, color: T.txt }}>Verticals</div>
              <div style={{ fontSize: 12, color: T.tx3, marginTop: 4 }}>
                Audiences your agent will target. Each vertical pairs with an ICP filter, template, and sender.
              </div>
            </div>
            <Btn primary onClick={() => {
              const id = "vertical_" + Date.now();
              const updated = [...(localConfig?.verticals || []), { id, label: "New Vertical", weight: 1 }];
              updateConfig("verticals", updated);
            }}>+ Add Vertical</Btn>
          </div>

          {/* Capacity summary card */}
          {(() => {
            const totalCap = localConfig?.max_leads_per_day || 1000;
            const vCount = (localConfig?.verticals || []).length || 1;
            const splitMode = localConfig?.split_mode || "even";
            const perV = Math.floor(totalCap / vCount);

            return (
              <div style={{ ...cardS, marginBottom: 16, padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, color: T.tx3, letterSpacing: ".04em", textTransform: "uppercase" }}>Daily capacity</div>
                    <div style={{ fontSize: 24, fontWeight: 300, color: T.txt, marginTop: 4 }}>
                      {totalCap.toLocaleString()} <span style={{ fontSize: 12, color: T.tx3, fontWeight: 400 }}>leads/day total</span>
                    </div>
                  </div>
                  {splitMode === "even" && vCount > 0 && (
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: T.tx3, letterSpacing: ".04em", textTransform: "uppercase" }}>Per vertical</div>
                      <div style={{ fontSize: 24, fontWeight: 300, color: T.blue, marginTop: 4 }}>
                        {perV.toLocaleString()} <span style={{ fontSize: 12, color: T.tx3, fontWeight: 400 }}>leads/day</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Split mode toggle */}
                <div style={{ display: "flex", gap: 4, padding: 4, background: "rgba(0,0,0,0.2)", borderRadius: 8 }}>
                  {[
                    { v: "even", l: "Even Split" },
                    { v: "weighted", l: "Weighted" },
                  ].map(mode => {
                    const active = (localConfig?.split_mode || "even") === mode.v;
                    return (
                      <button key={mode.v} onClick={() => updateConfig("split_mode", mode.v)} style={{
                        flex: 1, padding: "8px 16px", borderRadius: 6, fontFamily: "inherit", cursor: "pointer",
                        border: "none",
                        background: active ? "rgba(64,89,173,0.18)" : "transparent",
                        color: active ? T.blue : T.tx3,
                        fontSize: 12, fontWeight: 400,
                      }}>{mode.l}</button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Vertical list */}
          {(localConfig?.verticals || []).length === 0 ? (
            <div style={{ ...cardS, textAlign: "center", padding: 48, color: T.tx3, fontSize: 13 }}>
              No verticals yet. Click "+ Add Vertical" to define your first audience.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(localConfig?.verticals || []).map((v, vi) => {
                const totalWeight = (localConfig?.verticals || []).reduce((sum, x) => sum + (x.weight || 1), 0);
                const weightPct = Math.round(((v.weight || 1) / totalWeight) * 100);
                const totalCap = localConfig?.max_leads_per_day || 1000;
                const splitMode = localConfig?.split_mode || "even";
                const dailyAlloc = splitMode === "even"
                  ? Math.floor(totalCap / (localConfig?.verticals || []).length)
                  : Math.floor(totalCap * (v.weight || 1) / totalWeight);

                return (
                  <div key={v.id} style={{
                    display: "flex", alignItems: "center", gap: 16,
                    padding: "14px 18px",
                    background: "#2C2E33",
                    border: "1px solid rgba(217,219,221,0.08)",
                    borderRadius: 8,
                  }}>
                    {/* Drag handle dot */}
                    <div style={{ width: 6, height: 6, borderRadius: 3, background: "rgba(64,89,173,0.5)", flexShrink: 0 }} />

                    {/* Name input — borderless, inline */}
                    <input value={v.label || ""} onChange={e => {
                      const updated = [...(localConfig.verticals || [])];
                      updated[vi] = { ...updated[vi], label: e.target.value };
                      updateLocal("verticals", updated);
                    }} onBlur={() => flushConfig("verticals")}
                      style={{
                        flex: 1, padding: "6px 0", border: "none", background: "transparent",
                        color: T.txt, fontSize: 14, fontWeight: 400, fontFamily: "inherit", outline: "none",
                      }} placeholder="Vertical name" />

                    {/* Weight input (only in weighted mode) */}
                    {splitMode === "weighted" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input type="number" min={1} max={100} value={v.weight || 1} onChange={e => {
                          const updated = [...(localConfig.verticals || [])];
                          updated[vi] = { ...updated[vi], weight: parseInt(e.target.value) || 1 };
                          updateLocal("verticals", updated);
                        }} onBlur={() => flushConfig("verticals")}
                          style={{
                            width: 56, padding: "6px 8px", borderRadius: 6,
                            border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.2)",
                            color: T.txt, fontSize: 12, fontFamily: "inherit", textAlign: "center", outline: "none",
                          }} />
                        <span style={{ fontSize: 11, color: T.tx3, width: 32 }}>{weightPct}%</span>
                      </div>
                    )}

                    {/* Daily allocation */}
                    <div style={{ display: "flex", alignItems: "baseline", gap: 4, minWidth: 90, justifyContent: "flex-end" }}>
                      <span style={{ fontSize: 16, fontWeight: 400, color: T.blue }}>{dailyAlloc.toLocaleString()}</span>
                      <span style={{ fontSize: 10, color: T.tx3 }}>/day</span>
                    </div>

                    {/* Remove button — icon only */}
                    <button onClick={() => {
                      if (!window.confirm("Delete this vertical? Any ICP filters and templates using it will become orphaned.")) return;
                      const updated = (localConfig.verticals || []).filter((_, i) => i !== vi);
                      updateConfig("verticals", updated);
                    }} style={{
                      width: 28, height: 28, padding: 0, borderRadius: 6,
                      border: "1px solid rgba(217,219,221,0.08)",
                      background: "transparent", color: T.tx3,
                      fontSize: 14, cursor: "pointer", fontFamily: "inherit",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(239,68,68,0.3)"; e.currentTarget.style.color = T.red; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(217,219,221,0.08)"; e.currentTarget.style.color = T.tx3; }}
                      title="Remove vertical">×</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
         DOMAINS TAB
         ══════════════════════════════════════ */}
      {tab === "domains" && (
        <div>
          <div style={sectionLabel}>SENDING DOMAINS</div>
          <p style={{ fontSize: 12, color: T.tx3, marginBottom: 16, lineHeight: 1.5 }}>
            Select verified domains for the agent to send from. Each inbox sends up to {config?.max_emails_per_inbox || 20} emails per day.
          </p>

          {/* Add from verified domains */}
          {(() => {
            const alreadyAdded = (localConfig?.sending_domains || []).map(d => d.domain);
            const available = verifiedDomains.filter(d => !alreadyAdded.includes(d.domain));
            if (available.length > 0) return (
              <div style={{ ...cardS, marginBottom: 20, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 10 }}>Add verified domain</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {available.map(d => (
                    <button key={d.id} onClick={() => {
                      const updated = [...(localConfig?.sending_domains || []), { domain: d.domain, inboxes: ["outreach1@", "outreach2@", "outreach3@"] }];
                      updateConfig("sending_domains", updated);
                    }} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8,
                      border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.04)",
                      color: "#FAFAFA", fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(64,89,173,0.12)"; e.currentTarget.style.borderColor = "rgba(64,89,173,0.3)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(217,219,221,0.12)"; }}>
                      <div style={{ width: 6, height: 6, borderRadius: 3, background: "#2E8B57" }} />
                      {d.domain}
                      <span style={{ fontSize: 10, color: T.tx3 }}>+ Add</span>
                    </button>
                  ))}
                </div>
              </div>
            );
            if (alreadyAdded.length > 0 && available.length === 0) return (
              <div style={{ fontSize: 11, color: T.tx3, marginBottom: 16 }}>All verified domains have been added.</div>
            );
            return (
              <div style={{ ...cardS, marginBottom: 20, padding: 20, textAlign: "center" }}>
                <div style={{ fontSize: 12, color: T.tx3, marginBottom: 8 }}>No verified domains found.</div>
                <div style={{ fontSize: 11, color: T.tx3 }}>Add and verify domains in Email &gt; Campaigns &gt; Sending Domains first.</div>
              </div>
            );
          })()}

          {/* Active agent domains */}
          {(localConfig?.sending_domains || []).length > 0 && (
            <>
              <div style={sectionLabel}>ACTIVE AGENT DOMAINS ({(localConfig?.sending_domains || []).length})</div>
              {(localConfig?.sending_domains || []).map((domain, di) => {
                const inboxes = domain.inboxes || ["outreach1@", "outreach2@", "outreach3@"];
                const inboxCount = inboxes.length;
                const dailyCap = inboxCount * (config?.max_emails_per_inbox || 20);
                return (
                  <div key={di} style={{ ...cardS, marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 4, background: "#2E8B57" }} />
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>{domain.domain || "unnamed"}</div>
                          <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>{inboxCount} inboxes — {dailyCap} emails/day capacity</div>
                        </div>
                      </div>
                      <button onClick={() => {
                        const updated = (localConfig.sending_domains || []).filter((_, i) => i !== di);
                        updateConfig("sending_domains", updated);
                      }} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.08)", color: T.red, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Remove</button>
                    </div>
                    <label style={labelS}>SENDING INBOXES</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[0, 1, 2].map(slot => {
                        const prefix = (inboxes[slot] || "").replace(/@.*$/, "").replace(/@$/, "");
                        return (
                          <div key={slot} style={{ display: "flex", alignItems: "center", gap: 0 }}>
                            <div style={{ width: 24, height: 38, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: T.tx3 }}>{slot + 1}</div>
                            <input value={prefix} onChange={e => {
                              const updated = [...(localConfig.sending_domains || [])];
                              const newInboxes = [...inboxes];
                              newInboxes[slot] = e.target.value.replace(/[@\s]/g, "") + "@";
                              updated[di] = { ...updated[di], inboxes: newInboxes.filter(x => x && x !== "@") };
                              updateLocal("sending_domains", updated);
                            }} onBlur={() => flushConfig("sending_domains")}
                              style={{ flex: 1, padding: "8px 12px", borderRadius: "8px 0 0 8px", border: "1px solid rgba(217,219,221,0.12)", borderRight: "none", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 13, fontFamily: "inherit", outline: "none" }}
                              placeholder={slot === 0 ? "joe" : slot === 1 ? "hunter" : "florence"} />
                            <div style={{ padding: "8px 14px", borderRadius: "0 8px 8px 0", border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.03)", color: T.tx3, fontSize: 13, whiteSpace: "nowrap", userSelect: "none" }}>
                              @{domain.domain}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Total capacity summary */}
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(64,89,173,0.08)", border: "1px solid rgba(64,89,173,0.15)", marginTop: 8 }}>
                <div style={{ fontSize: 12, color: "#FAFAFA" }}>
                  Total capacity: {(localConfig?.sending_domains || []).reduce((sum, d) => sum + (d.inboxes || []).length, 0)} inboxes x {config?.max_emails_per_inbox || 20}/day = <span style={{ fontWeight: 500, color: T.blue }}>{(localConfig?.sending_domains || []).reduce((sum, d) => sum + (d.inboxes || []).length, 0) * (config?.max_emails_per_inbox || 20)} emails/day</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
         TEMPLATES TAB
         ══════════════════════════════════════ */}
      {tab === "templates" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={sectionLabel}>EMAIL TEMPLATES (ONE PER VERTICAL)</div>
            <Btn primary disabled={verticals.length === 0} onClick={() => {
              if (verticals.length === 0) return;
              setEditingSequence({ name: "", vertical: verticalIds[0], steps: [{ step: 1, subject: "", body: "", delay_days: 0 }], is_active: true });
            }}>
              + New Template
            </Btn>
          </div>

          {verticals.length === 0 ? (
            <div style={{ ...cardS, textAlign: "center", padding: 40, color: T.tx3, fontSize: 13 }}>
              Define your verticals first in the Verticals tab.
            </div>
          ) : sequences.length === 0 && !editingSequence ? (
            <div style={{ ...cardS, textAlign: "center", padding: 40, color: T.tx3, fontSize: 13 }}>
              No email templates yet. Create one per vertical for the agent to use.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {sequences.map(seq => (
                <div key={seq.id} style={cardS}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>{seq.name}</div>
                      <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>
                        {verticalLabel(seq.vertical)} — {seq.total_enrolled || 0} enrolled, {seq.total_replied || 0} replied
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Badge>{seq.is_active ? "Active" : "Paused"}</Badge>
                      <button onClick={() => setEditingSequence({ ...seq })} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                    </div>
                  </div>
                  {seq.steps && seq.steps[0] && (
                    <div style={{ marginTop: 12, padding: 14, background: "rgba(255,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
                      <div style={{ fontSize: 11, color: T.blue, marginBottom: 4 }}>Subject: {seq.steps[0].subject}</div>
                      <div style={{ fontSize: 11, color: T.tx3, whiteSpace: "pre-wrap", maxHeight: 80, overflow: "hidden" }}>{seq.steps[0].body}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Edit/Create Template Modal */}
          {editingSequence && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
              onClick={e => { if (e.target === e.currentTarget) setEditingSequence(null); }}>
              <div style={{ width: 600, maxWidth: "90vw", maxHeight: "85vh", overflow: "auto", background: T.bg, border: "1px solid rgba(217,219,221,0.08)", borderRadius: 14, padding: 28, boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
                  <h2 style={{ fontSize: 16, fontWeight: 400, color: T.txt, margin: 0 }}>{editingSequence.id ? "Edit Template" : "New Template"}</h2>
                  <button onClick={() => setEditingSequence(null)} style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer" }}><Icon name="x" size={18} /></button>
                </div>

                <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelS}>Template Name</label>
                    <input value={editingSequence.name} onChange={e => setEditingSequence(p => ({ ...p, name: e.target.value }))} style={inputS} placeholder="Recruiting Cold Outreach" />
                  </div>
                  <div style={{ width: 200 }}>
                    <label style={labelS}>Vertical</label>
                    <select value={editingSequence.vertical} onChange={e => setEditingSequence(p => ({ ...p, vertical: e.target.value }))} style={selectS}>
                      {verticals.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                    </select>
                  </div>
                </div>

                <label style={labelS}>Subject Line</label>
                <input value={editingSequence.steps?.[0]?.subject || ""} onChange={e => {
                  const steps = [{ ...(editingSequence.steps?.[0] || {}), step: 1, subject: e.target.value }];
                  setEditingSequence(p => ({ ...p, steps }));
                }} style={{ ...inputS, marginBottom: 12 }} placeholder="{{first_name}}, 25 free leads as promised" />

                <label style={labelS}>Email Body</label>
                <textarea value={editingSequence.steps?.[0]?.body || ""} onChange={e => {
                  const steps = [{ ...(editingSequence.steps?.[0] || {}), step: 1, body: e.target.value }];
                  setEditingSequence(p => ({ ...p, steps }));
                }} style={{ ...inputS, height: 200, fontFamily: "monospace", fontSize: 12, marginBottom: 12 }}
                  placeholder={"Hey {{first_name}},\n\nI am going to give you back revenue, control and autonomy.\n\nWe built a software for recruiting firms that replaces your CRM, dialer, email, SMS, project management, AI, the whole stack. I put together a 2 minute video showing how it works for your business.\n\nMind if I share it here?\n\nP.S. If you watch it and don't find it valuable I'll send you 25 verified leads in your ICP"} />

                <div style={{ fontSize: 10, color: T.tx3, marginBottom: 16 }}>
                  Available merge fields: {"{{first_name}} {{last_name}} {{full_name}} {{company}} {{title}} {{email}}"}
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt, marginBottom: 16 }}>
                  <input type="checkbox" checked={editingSequence.is_active !== false} onChange={e => setEditingSequence(p => ({ ...p, is_active: e.target.checked }))} style={{ accentColor: T.blue }} />
                  Active
                </label>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => setEditingSequence(null)} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                  <Btn primary onClick={() => saveSequence(editingSequence)} disabled={saving}>{saving ? "Saving..." : "Save Template"}</Btn>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
         ICP FILTERS TAB
         ══════════════════════════════════════ */}
      {tab === "icp" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div style={sectionLabel}>VERTICAL → SNOV LIST MAPPING</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={loadSnovLists} disabled={snovListsLoading}>
                {snovListsLoading ? "Refreshing..." : "↻ Refresh Lists"}
              </Btn>
              <Btn primary onClick={() => {
                const firstVertical = (verticals[0] || {}).id || "";
                const filters = [...(localConfig?.icp_filters || []), {
                  vertical: firstVertical,
                  owner_id: "",
                  owner_email: "",
                  from_name: "",
                  sender_name: "",
                  snov_list_id: null,
                  positions: [],
                  daily_limit: 50,
                }];
                updateConfig("icp_filters", filters);
              }}>+ Add Mapping</Btn>
            </div>
          </div>

          {/* Snov credentials check */}
          {(!localConfig?.snov_client_id || !localConfig?.snov_client_secret) && (
            <div style={{ ...cardS, marginBottom: 16, padding: 14, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)" }}>
              <div style={{ fontSize: 12, color: T.amber || "#f59e0b" }}>Add your Snov.io API credentials in the <strong>Settings</strong> tab first. The agent needs them to fetch your prospect lists.</div>
            </div>
          )}

          {snovListsError && (
            <div style={{ ...cardS, marginBottom: 16, padding: 14, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
              <div style={{ fontSize: 12, color: T.red }}>Snov error: {snovListsError}</div>
            </div>
          )}

          {/* Compute available sender prefixes from configured domains */}
          {(() => {
            const allInboxes = (localConfig?.sending_domains || []).flatMap(d => (d.inboxes || []).map(i => i.replace(/@.*$/, "").toLowerCase()));
            const uniquePrefixes = [...new Set(allInboxes)].filter(Boolean);
            window.__agent_prefixes = uniquePrefixes;
            return null;
          })()}

          {(localConfig?.icp_filters || []).length === 0 ? (
            <div style={{ ...cardS, textAlign: "center", padding: 40, color: T.tx3, fontSize: 13 }}>
              No mappings configured. Click <strong>+ Add Mapping</strong> to map a vertical to a Snov.io list.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {(localConfig?.icp_filters || []).map((filter, fi) => {
                const senderPrefixes = window.__agent_prefixes || [];
                const updateFilter = (changes) => {
                  const updated = [...(localConfig.icp_filters || [])];
                  updated[fi] = { ...updated[fi], ...changes };
                  updateConfig("icp_filters", updated);
                };
                const selectedList = snovLists.find(l => String(l.id) === String(filter.snov_list_id));

                return (
                  <div key={fi} style={cardS}>
                    {/* Row 1: Vertical, Owner, Daily Limit, Remove */}
                    <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
                      <div style={{ flex: 1, minWidth: 150 }}>
                        <label style={labelS}>Vertical</label>
                        <select value={filter.vertical || ""} onChange={e => updateFilter({ vertical: e.target.value })} onBlur={() => flushConfig("icp_filters")} style={selectS}>
                          <option value="">— Select vertical —</option>
                          {verticals.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 2, minWidth: 220 }}>
                        <label style={labelS}>Owner (sender + assignee)</label>
                        <select
                          value={filter.owner_id || ""}
                          onChange={e => {
                            const userId = e.target.value;
                            const user = teamMembers.find(u => u.id === userId);
                            if (user) {
                              const emailPrefix = (user.email || "").split("@")[0].toLowerCase().trim();
                              const fullName = ((user.first_name || "") + " " + (user.last_name || "")).trim();
                              updateFilter({
                                owner_id: user.id,
                                owner_email: user.email,
                                from_name: fullName,
                                sender_name: emailPrefix,
                              });
                            } else {
                              updateFilter({ owner_id: "", owner_email: "", from_name: "", sender_name: "" });
                            }
                          }}
                          style={selectS}
                        >
                          <option value="">— Select owner —</option>
                          {teamMembers.length === 0 && <option value="" disabled>No team members loaded</option>}
                          {teamMembers.map(u => {
                            const fullName = ((u.first_name || "") + " " + (u.last_name || "")).trim();
                            const prefix = (u.email || "").split("@")[0];
                            return (
                              <option key={u.id} value={u.id}>
                                {fullName} ({prefix}@)
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div style={{ width: 100 }}>
                        <label style={labelS}>Daily Limit</label>
                        <input type="number" min={1} max={500} value={filter.daily_limit || 50} onChange={e => updateFilter({ daily_limit: parseInt(e.target.value) || 50 })} onBlur={() => flushConfig("icp_filters")} style={inputS} />
                      </div>
                      <button onClick={() => {
                        const updated = (localConfig.icp_filters || []).filter((_, i) => i !== fi);
                        updateConfig("icp_filters", updated);
                      }} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.08)", color: T.red, fontSize: 10, cursor: "pointer", fontFamily: "inherit", height: 38 }}>Remove</button>
                    </div>

                    {/* Live preview of the from header */}
                    {filter.from_name && filter.sender_name && (
                      <div style={{ marginBottom: 12, padding: 10, borderRadius: 6, background: "rgba(255,255,255,0.03)", fontSize: 11, color: T.tx3 }}>
                        Emails will appear as: <span style={{ color: T.txt }}>{filter.from_name}</span> <span style={{ color: T.tx3 }}>&lt;{filter.sender_name}@&lt;rotating-domain&gt;&gt;</span>
                        <br/>
                        <span style={{ color: T.tx3 }}>Replies, tasks, and deals will be assigned to this owner.</span>
                      </div>
                    )}

                    {/* Snov List dropdown */}
                    <div style={{ padding: 14, borderRadius: 8, background: "rgba(64,89,173,0.06)", border: "1px solid rgba(64,89,173,0.15)" }}>
                      <label style={{ ...labelS, color: T.blue, marginBottom: 8 }}>SNOV.IO PROSPECT LIST</label>
                      {snovLists.length === 0 ? (
                        <div style={{ fontSize: 11, color: T.tx3, lineHeight: 1.6 }}>
                          {snovListsLoading ? "Loading lists from Snov.io..." : (
                            <>
                              No lists found. <a href="https://app.snov.io/prospects/list" target="_blank" rel="noopener noreferrer" style={{ color: T.blue }}>Open Snov.io</a> to create one, then click <strong>↻ Refresh Lists</strong> above.
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          <select
                            value={filter.snov_list_id || ""}
                            onChange={e => updateFilter({ snov_list_id: e.target.value || null })}
                            onBlur={() => flushConfig("icp_filters")}
                            style={selectS}
                          >
                            <option value="">— Select a Snov list —</option>
                            {snovLists.map(l => (
                              <option key={l.id} value={l.id}>{l.name} ({l.count} prospects)</option>
                            ))}
                          </select>
                          {selectedList && (
                            <div style={{ marginTop: 8, fontSize: 11, color: T.tx3 }}>
                              Pulling from <strong style={{ color: T.txt }}>{selectedList.name}</strong> — {selectedList.count} prospects available. At {filter.daily_limit || 50}/day, this list lasts ~{Math.ceil(selectedList.count / (filter.daily_limit || 50))} days.
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Optional client-side title filter */}
                    <div style={{ marginTop: 12 }}>
                      <label style={labelS}>Job Title Filter (optional, comma separated)</label>
                      <CommaInput value={filter.positions} onCommit={arr => updateFilter({ positions: arr })} style={inputS} placeholder="CEO, Founder, Owner — leave blank to use everyone in the list" />
                      <div style={{ marginTop: 4, fontSize: 10, color: T.tx3 }}>If set, only prospects whose job title contains one of these words will be imported.</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
         SETTINGS TAB
         ══════════════════════════════════════ */}
      {tab === "settings" && (
        <div>
          {/* Snov.io Credentials */}
          <div style={sectionLabel}>SNOV.IO API</div>
          <div style={{ ...cardS, marginBottom: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <div>
                <label style={labelS}>Client ID</label>
                <input value={localConfig?.snov_client_id || ""} onChange={e => updateLocal("snov_client_id", e.target.value)} onBlur={() => flushConfig("snov_client_id")} style={inputS} placeholder="Your Snov.io Client ID" />
              </div>
              <div>
                <label style={labelS}>Client Secret</label>
                <input type="password" value={localConfig?.snov_client_secret || ""} onChange={e => updateLocal("snov_client_secret", e.target.value)} onBlur={() => flushConfig("snov_client_secret")} style={inputS} placeholder="Your Snov.io Client Secret" />
              </div>
            </div>
          </div>

          {/* Daily Limits */}
          <div style={sectionLabel}>DAILY LIMITS</div>
          <div style={{ ...cardS, marginBottom: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 14 }}>
              <div>
                <label style={labelS}>Max Leads Per Day</label>
                <input type="number" value={config?.max_leads_per_day || 100} onChange={e => updateConfig("max_leads_per_day", parseInt(e.target.value) || 100)} style={inputS} />
              </div>
              <div>
                <label style={labelS}>Max Emails Per Inbox</label>
                <input type="number" value={config?.max_emails_per_inbox || 20} onChange={e => updateConfig("max_emails_per_inbox", parseInt(e.target.value) || 20)} style={inputS} />
              </div>
              <div>
                <label style={labelS}>Warmup Day (auto-calculated)</label>
                <input type="number" value={config?.warmup_day || 0} disabled style={{ ...inputS, opacity: 0.5 }} />
              </div>
            </div>
          </div>

          {/* Approval */}
          <div style={sectionLabel}>EMAIL APPROVAL</div>
          <div style={{ ...cardS, marginBottom: 20 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={config?.require_approval !== false}
                onChange={e => updateConfig("require_approval", e.target.checked)}
                style={{ accentColor: T.blue, marginTop: 2 }}
              />
              <div>
                <div style={{ fontSize: 12, color: T.txt, marginBottom: 4 }}>Require approval before sending (recommended)</div>
                <div style={{ fontSize: 11, color: T.tx3, lineHeight: 1.5 }}>
                  When on, newly sourced emails land in the <strong>Approval Queue</strong> tab as <strong>pending_approval</strong>. The sender won't touch them until you click Approve. Turn off only when you trust the system fully.
                </div>
              </div>
            </label>
          </div>

          {/* Sending Window */}
          <div style={sectionLabel}>SENDING WINDOW</div>
          <div style={{ ...cardS, marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: T.tx3, marginBottom: 14, lineHeight: 1.5 }}>
              The agent will only send emails during these hours. Outside this window, queued emails wait until the window opens again.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelS}>Start Hour (24h)</label>
                <select value={config?.send_hour_start ?? 6} onChange={e => updateConfig("send_hour_start", parseInt(e.target.value))} style={selectS}>
                  {Array.from({ length: 24 }, (_, i) => i).map(h => (
                    <option key={h} value={h}>{h.toString().padStart(2, "0")}:00 ({h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelS}>End Hour (24h)</label>
                <select value={config?.send_hour_end ?? 19} onChange={e => updateConfig("send_hour_end", parseInt(e.target.value))} style={selectS}>
                  {Array.from({ length: 24 }, (_, i) => i + 1).map(h => (
                    <option key={h} value={h}>{h.toString().padStart(2, "0")}:00 ({h === 24 ? "12am" : h === 12 ? "12pm" : h < 12 ? `${h}am` : `${h - 12}pm`})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelS}>Timezone</label>
                <select value={config?.send_timezone || "America/New_York"} onChange={e => updateConfig("send_timezone", e.target.value)} style={selectS}>
                  <option value="America/New_York">Eastern (New York)</option>
                  <option value="America/Chicago">Central (Chicago)</option>
                  <option value="America/Denver">Mountain (Denver)</option>
                  <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                  <option value="America/Phoenix">Arizona (Phoenix)</option>
                  <option value="America/Toronto">Eastern Canada (Toronto)</option>
                  <option value="Europe/London">UK (London)</option>
                  <option value="Europe/Berlin">Central Europe (Berlin)</option>
                  <option value="Australia/Sydney">Australia (Sydney)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt }}>
              <input type="checkbox" checked={config?.send_weekends || false} onChange={e => updateConfig("send_weekends", e.target.checked)} style={{ accentColor: T.blue }} />
              Send on weekends (Saturday & Sunday)
            </label>
            <div style={{ marginTop: 12, padding: 12, background: "rgba(64,89,173,0.06)", borderRadius: 6, fontSize: 11, color: T.tx3 }}>
              Current window: <strong style={{ color: T.blue }}>
                {(config?.send_hour_start ?? 6).toString().padStart(2, "0")}:00 — {(config?.send_hour_end ?? 19).toString().padStart(2, "0")}:00 {config?.send_timezone || "America/New_York"}
              </strong>
              {!config?.send_weekends && " (weekdays only)"}
            </div>
          </div>

          {/* Warmup */}
          <div style={sectionLabel}>WARMUP</div>
          <div style={{ ...cardS, marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt }}>
                <input type="checkbox" checked={config?.warmup_enabled || false} onChange={e => updateConfig("warmup_enabled", e.target.checked)} style={{ accentColor: T.blue }} />
                Enable Warmup (auto-ramp send volume)
              </label>
            </div>
            <div style={{ padding: 14, background: "rgba(255,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
              <div style={{ fontSize: 11, color: T.tx3, marginBottom: 8 }}>WARMUP SCHEDULE</div>
              <div style={{ fontSize: 12, color: T.txt, lineHeight: 1.8 }}>
                Days 1-5: 15 emails per inbox per day<br/>
                Days 6-10: 18 emails per inbox per day<br/>
                Day 11+: 20 emails per inbox per day (full speed)
              </div>
              {config?.warmup_started_at && (
                <div style={{ fontSize: 10, color: T.tx3, marginTop: 8 }}>
                  Warmup started: {cfDate(config.warmup_started_at)} — Day {Math.floor((Date.now() - new Date(config.warmup_started_at).getTime()) / 86400000) + 1}
                </div>
              )}
            </div>
          </div>

          {/* Auto-Reply */}
          <div style={sectionLabel}>AUTO-REPLY (SEND VIDEO ON REPLY)</div>
          <div style={{ ...cardS, marginBottom: 20 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt, marginBottom: 14 }}>
              <input type="checkbox" checked={config?.auto_reply_enabled || false} onChange={e => updateConfig("auto_reply_enabled", e.target.checked)} style={{ accentColor: T.blue }} />
              Auto-send video link when someone replies positively
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <div>
                <label style={labelS}>Reply Detection Keywords (comma separated)</label>
                <input value={(localConfig?.auto_reply_keywords || ["yes", "sure", "send it", "interested", "love to", "please", "go ahead"]).join(", ")}
                  onChange={e => updateLocal("auto_reply_keywords", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} onBlur={() => flushConfig("auto_reply_keywords")}
                  style={inputS} />
              </div>
              <div>
                <label style={labelS}>Reply Delay (seconds, 0 for instant)</label>
                <input type="number" value={config?.auto_reply_delay || 0} onChange={e => updateConfig("auto_reply_delay", parseInt(e.target.value) || 0)} style={inputS} />
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={labelS}>Auto-Reply Template</label>
              <textarea value={localConfig?.auto_reply_template || "Hey {{first_name}},\n\nAppreciate you getting back to me. Here's that 2 minute video I mentioned:\n\n{{video_link}}\n\nLet me know what you think — happy to walk through anything live if it makes sense."}
                onChange={e => updateLocal("auto_reply_template", e.target.value)} onBlur={() => flushConfig("auto_reply_template")}
                style={{ ...inputS, height: 120, fontFamily: "monospace", fontSize: 12 }} />
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={labelS}>Video Links Per Vertical</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                {verticals.length === 0 && <div style={{ fontSize: 11, color: T.tx3 }}>Add verticals first in the Verticals tab.</div>}
                {verticals.map(v => (
                  <div key={v.id} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: T.tx3, width: 140 }}>{v.label}</span>
                    <input value={(localConfig?.video_links || {})[v.id] || ""} onChange={e => {
                      const links = { ...(localConfig?.video_links || {}), [v.id]: e.target.value };
                      updateLocal("video_links", links);
                    }} onBlur={() => flushConfig("video_links")} style={{ ...inputS, flex: 1 }} placeholder="https://www.loom.com/share/..." />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Multichannel Escalation */}
          <div style={sectionLabel}>MULTICHANNEL ESCALATION</div>
          <div style={{ ...cardS, marginBottom: 20 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt, marginBottom: 16 }}>
              <input type="checkbox" checked={config?.escalation_enabled || false} onChange={e => updateConfig("escalation_enabled", e.target.checked)} style={{ accentColor: T.blue }} />
              Enable multichannel escalation for non-responders
            </label>

            {config?.escalation_enabled && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt }}>
                    <input type="checkbox" checked={config?.escalation_call !== false} onChange={e => updateConfig("escalation_call", e.target.checked)} style={{ accentColor: T.blue }} />
                    Create call task
                  </label>
                  <div style={{ width: 120 }}>
                    <label style={labelS}>After days</label>
                    <input type="number" min={1} max={30} value={config?.escalation_call_days || 3} onChange={e => updateConfig("escalation_call_days", parseInt(e.target.value) || 3)} style={inputS} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt }}>
                    <input type="checkbox" checked={config?.escalation_sms !== false} onChange={e => updateConfig("escalation_sms", e.target.checked)} style={{ accentColor: T.blue }} />
                    Send SMS follow-up
                  </label>
                  <div style={{ width: 120 }}>
                    <label style={labelS}>After days</label>
                    <input type="number" min={1} max={30} value={config?.escalation_sms_days || 7} onChange={e => updateConfig("escalation_sms_days", parseInt(e.target.value) || 7)} style={inputS} />
                  </div>
                </div>
                <div style={{ padding: 12, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.06)", fontSize: 11, color: T.tx3, lineHeight: 1.6 }}>
                  Day 0: Agent sends cold email<br/>
                  Day {config?.escalation_call_days || 3}: No reply — auto-creates call task for contact owner<br/>
                  Day {config?.escalation_sms_days || 7}: Still no reply — sends SMS if phone number exists
                </div>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div style={sectionLabel}>AGENT CONTROL</div>
          <div style={cardS}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 13, color: T.txt }}>Pause Agent</div>
                  <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>Stop all sourcing and sending. Queued emails will be held.</div>
                </div>
                <button onClick={toggleAgent} style={{
                  padding: "8px 18px", borderRadius: 8, border: "1px solid " + (config?.enabled ? T.red + "40" : T.grn + "40"),
                  background: config?.enabled ? T.red + "12" : T.grn + "12",
                  color: config?.enabled ? T.red : T.grn, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                }}>{config?.enabled ? "Pause" : "Resume"}</button>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid " + T.bdr, paddingTop: 14 }}>
                <div>
                  <div style={{ fontSize: 13, color: T.txt }}>Flush Send Queue</div>
                  <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>Cancel all queued emails that haven't been sent yet.</div>
                </div>
                <button onClick={async () => {
                  if (!window.confirm("Cancel all queued emails? This cannot be undone.")) return;
                  await supabase.from("outbound_send_queue").update({ status: "skipped" }).eq("org_id", orgId).eq("status", "queued");
                  loadDashboard();
                }} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid " + T.amb + "40", background: T.amb + "12", color: T.amb, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Flush Queue</button>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid " + T.bdr, paddingTop: 14 }}>
                <div>
                  <div style={{ fontSize: 13, color: T.red }}>Kill Switch</div>
                  <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>Immediately stop everything — pause agent and flush all queued emails.</div>
                </div>
                <button onClick={async () => {
                  if (!window.confirm("KILL SWITCH: Pause agent AND flush all queued emails?")) return;
                  await updateConfig("enabled", false);
                  await supabase.from("outbound_send_queue").update({ status: "skipped" }).eq("org_id", orgId).eq("status", "queued");
                  loadDashboard();
                }} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid " + T.red, background: T.red + "20", color: T.red, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>KILL SWITCH</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
